import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Hexagon, Lock } from "lucide-react";

const TASKS = [
    {
        id: "swap",
        label: "DEX Swap",
        req: 'agent.request("swap", { pair: "ETH/USDC", size: "0.40 ETH" })',
        lines: [
            ["identity", "suid:0x7f3e…a91c · human:0x0a34…9d21", "BOUND"],
            ["policy", "limits ok · slippage ≤ 0.5% · kill-switch armed", null],
            ["signing", "ed25519 · ts 1729…4821", null],
            ["receipt", "0x9f2e…c4d7", "VERIFIED"],
        ],
    },
    {
        id: "oracle",
        label: "Oracle Fetch",
        req: 'agent.request("oracle.fetch", { feed: "ETH/USD" })',
        lines: [
            ["identity", "suid:0x7f3e…a91c · human:0x0a34…9d21", "BOUND"],
            ["route", "edge:api.authena.xyz/v1 · 11ms", null],
            ["signing", "ed25519 · ts 1729…4837", null],
            ["receipt", "0x77aa…01be", "VERIFIED"],
        ],
    },
    {
        id: "zk",
        label: "Prove Humanity",
        req: 'agent.request("zk.prove", { scheme: "humanity-v1" })',
        lines: [
            ["identity", "suid:0x7f3e…a91c · human:0x0a34…9d21", "BOUND"],
            ["zk", "circuit humanity-v1 · witness sealed", null],
            ["verify", "proof check · 9ms · private", null],
            ["receipt", "0xd1c3…8f52", "VERIFIED"],
        ],
    },
];

export default function SandboxCard() {
    const [task, setTask] = useState(TASKS[0]);
    const [shown, setShown] = useState(0);
    const [running, setRunning] = useState(false);
    const timers = useRef([]);

    const run = (t) => {
        if (running) return;
        setTask(t);
        setRunning(true);
        setShown(0);
        timers.current.forEach(clearTimeout);
        timers.current = [];
        t.lines.forEach((_, i) => {
            timers.current.push(
                setTimeout(() => {
                    setShown(i + 1);
                    if (i === t.lines.length - 1) setRunning(false);
                }, 550 + i * 480)
            );
        });
    };

    useEffect(() => {
        const t = setTimeout(() => run(TASKS[0]), 1400);
        const timersRef = timers;
        return () => {
            clearTimeout(t);
            timersRef.current.forEach(clearTimeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const verified = !running && shown === task.lines.length && shown > 0;

    return (
        <div
            data-testid="hero-sandbox-card"
            className="glass overflow-hidden rounded-2xl border border-white/10 shadow-2xl glow-cyan"
        >
            <div className="scanlines flex items-center justify-between border-b border-white/10 px-5 py-3.5">
                <div className="flex items-center gap-2.5">
                    <span className="pulse-dot h-2 w-2 rounded-full bg-neon-emerald" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300">
                        Agent sandbox · live
                    </span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">v1.4</span>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-5 py-4">
                <div className="flex items-center gap-2 font-mono text-xs text-slate-400">
                    <Hexagon size={14} className="text-neon-cyan" />
                    suid:0x7f3e…a91c
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-neon-emerald/30 bg-neon-emerald/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-neon-emerald">
                    <Lock size={10} /> Human-bound
                </span>
            </div>

            <div className="flex flex-wrap gap-2 px-5 pt-4">
                {TASKS.map((t) => (
                    <button
                        key={t.id}
                        data-testid={`sandbox-task-${t.id}`}
                        onClick={() => run(t)}
                        disabled={running}
                        className={`rounded-full border px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] transition-all duration-300 disabled:opacity-50 ${
                            task.id === t.id
                                ? "border-neon-cyan/50 bg-neon-cyan/10 text-neon-cyan"
                                : "border-white/10 text-slate-400 hover:border-neon-cyan/30 hover:text-slate-200"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="px-5 pb-5 pt-4">
                <div className="min-h-[172px] rounded-xl border border-white/5 bg-black/40 p-4 font-mono text-[11px] leading-relaxed">
                    <p className="text-slate-300">
                        <span className="text-neon-cyan">&gt;</span> {task.req}
                    </p>
                    {task.lines.slice(0, shown).map(([key, val, badge], i) => (
                        <p key={`${task.id}-${i}`} className="mt-1.5 flex items-start gap-3">
                            <span className="w-16 shrink-0 text-neon-violet">{key}</span>
                            <span className="flex-1 break-all text-slate-400">{val}</span>
                            {badge && (
                                <span
                                    className={`shrink-0 rounded border px-1.5 text-[9px] uppercase tracking-[0.16em] ${
                                        badge === "VERIFIED"
                                            ? "border-neon-emerald/40 text-neon-emerald"
                                            : "border-neon-cyan/40 text-neon-cyan"
                                    }`}
                                >
                                    {badge}
                                </span>
                            )}
                        </p>
                    ))}
                    {running && (
                        <p className="mt-1.5 text-slate-500">
                            <span className="inline-block h-3 w-1.5 animate-pulse bg-neon-cyan/70 align-middle" />
                        </p>
                    )}
                </div>

                <div
                    className={`mt-3 flex items-center gap-2 rounded-lg border px-3.5 py-2.5 transition-all duration-500 ${
                        verified
                            ? "border-neon-emerald/30 bg-neon-emerald/5 opacity-100"
                            : "border-white/5 bg-white/[0.02] opacity-40"
                    }`}
                    data-testid="sandbox-status"
                >
                    <CheckCircle2 size={15} className={verified ? "text-neon-emerald" : "text-slate-600"} />
                    <span
                        className={`font-mono text-[11px] uppercase tracking-[0.18em] ${
                            verified ? "text-neon-emerald" : "text-slate-500"
                        }`}
                    >
                        {verified ? "Proof published to settlement layer" : "Awaiting execution"}
                    </span>
                </div>
            </div>
        </div>
    );
}